import { html, nothing } from "lit";
import { renderHelpArticleScreenshots } from "../help/help-article-screenshots.ts";
import {
  getHelpArticle,
  listHelpTopicsGrouped,
  type HelpArticle,
  type HelpTopicId,
} from "../help/help-content.ts";
import { icons } from "../icons.ts";
import { iconForTab, titleForTab, type Tab } from "../navigation.ts";
import { renderFleetSystemConceptsPanel } from "./fleet-system-concepts-panel.ts";

export type HelpViewProps = {
  topic: HelpTopicId;
  onSelectTopic: (topic: HelpTopicId) => void;
  onOpenTab: (tab: Tab) => void;
  onOpenFleetGuide?: () => void;
};

export function renderHelp(props: HelpViewProps) {
  const article = getHelpArticle(props.topic);

  return html`
    <section class="help-layout">
      <aside class="help-sidebar" aria-label="Help topics">
        ${listHelpTopicsGrouped().map(
          (group) => html`
            <div class="help-sidebar__group">
              <div class="help-sidebar__label">${group.label}</div>
              <ul class="help-sidebar__list">
                ${group.topics.map((topicId) =>
                  renderHelpNavItem(topicId, props.topic === topicId, props.onSelectTopic),
                )}
              </ul>
            </div>
          `,
        )}
      </aside>
      <article class="help-article">
        ${renderHelpArticle(article, props.onOpenTab, props.onOpenFleetGuide)}
      </article>
    </section>
  `;
}

function renderHelpNavItem(
  topicId: HelpTopicId,
  active: boolean,
  onSelect: (topic: HelpTopicId) => void,
) {
  const article = getHelpArticle(topicId);
  const iconKey =
    topicId === "intro" ||
    topicId === "fleet-architecture" ||
    topicId === "fleet-self-improvement" ||
    topicId === "fleet-self-improvement-local" ||
    topicId === "fleet-projects" ||
    topicId === "fleet-work-plan" ||
    topicId === "fleet-session-environments"
      ? "book"
      : iconForTab(topicId as Tab);
  return html`
    <li>
      <button
        type="button"
        class="help-sidebar__item ${active ? "help-sidebar__item--active" : ""}"
        @click=${() => onSelect(topicId)}
        aria-current=${active ? "page" : nothing}
      >
        <span class="help-sidebar__icon" aria-hidden="true">${icons[iconKey]}</span>
        <span class="help-sidebar__text">
          <span class="help-sidebar__title">${article.title}</span>
          <span class="help-sidebar__tagline">${article.tagline}</span>
        </span>
      </button>
    </li>
  `;
}

function renderHelpArticle(
  article: HelpArticle,
  onOpenTab: (tab: Tab) => void,
  onOpenFleetGuide?: () => void,
) {
  const openTabButton = article.openTab
    ? html`
        <button
          type="button"
          class="btn primary help-article__open"
          @click=${() => onOpenTab(article.openTab!)}
        >
          Open ${titleForTab(article.openTab!)}
        </button>
      `
    : nothing;

  if (article.id === "fleet-architecture") {
    return html`
      <header class="help-article__header">
        <h2 class="help-article__title">${article.title}</h2>
        <p class="help-article__tagline">${article.tagline}</p>
        ${openTabButton}
      </header>
      <div class="help-article__body help-article__body--fleet-architecture">
        ${renderFleetSystemConceptsPanel({ onOpenGuide: onOpenFleetGuide, diagramOpen: true })}
      </div>
    `;
  }

  return html`
    <header class="help-article__header">
      <h2 class="help-article__title">${article.title}</h2>
      <p class="help-article__tagline">${article.tagline}</p>
      ${openTabButton}
    </header>
    <div class="help-article__body">
      <section class="help-block">
        <h3 class="help-block__title">What it is</h3>
        <p class="help-block__text">${article.overview}</p>
      </section>
      ${renderHelpArticleScreenshots(article.images)}
      <section class="help-block">
        <h3 class="help-block__title">How to use it</h3>
        <ol class="help-steps">
          ${article.steps.map((step) => html`<li>${step}</li>`)}
        </ol>
      </section>
      ${
        article.tips.length > 0
          ? html`
              <section class="help-block help-block--tips">
                <h3 class="help-block__title">Tips</h3>
                <ul class="help-tips">
                  ${article.tips.map((tip) => html`<li>${tip}</li>`)}
                </ul>
              </section>
            `
          : nothing
      }
    </div>
  `;
}
